/*
 * State / province subdivisions for the countries where we collect one.
 *
 * Mirror of the canonical backend list in `backend/src/util/states.ts`; the
 * backend's `tests/core/signup_state.test.ts` parses this file and fails if the
 * two drift, the same guard the country lists carry. Add a code here and it must
 * be added there too.
 *
 * Codes are ISO 3166-2 subdivision codes without the country prefix — `CA`, not
 * `US-CA` — because that is the alphabet Stripe stores in
 * `individual.address.state`, and a state read back off a Connect account
 * overwrites the one collected here (Trello 215).
 */

export const usStates = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
  AS: 'American Samoa',
  GU: 'Guam',
  MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico',
  VI: 'U.S. Virgin Islands'
};

export const caProvinces = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon'
};

export const statesByCountry = {
  us: usStates,
  ca: caProvinces
};

// Does this country get a state field at all? Everyone outside US/CA does not,
// which is why the dropdown is absent rather than empty for most guides.
export const countryCollectsState = country =>
  Boolean(country) &&
  Object.prototype.hasOwnProperty.call(statesByCountry, country.toLowerCase());

// The `{ code: label }` map to render, or an empty object for a country that
// doesn't collect one.
export const statesForCountry = country =>
  countryCollectsState(country) ? statesByCountry[country.toLowerCase()] : {};

// "Alberta" vs "State" — Canadians don't have states, and a form that tells them
// they do reads as one built for somewhere else.
export const stateLabelForCountry = country =>
  String(country).toLowerCase() === 'ca' ? 'Province' : 'State';

/*
 * Is the currently-selected state valid for the currently-selected country?
 *
 * True when the country doesn't collect one at all, so callers can use this as
 * the whole client-side validity test without special-casing. The backend does
 * not enforce this — it silently drops anything it doesn't recognise — so this
 * is what actually stops a guide submitting a blank one.
 */
export const isValidStateForCountry = (country, state) => {
  if (!countryCollectsState(country)) return true;
  return Object.prototype.hasOwnProperty.call(
    statesForCountry(country),
    String(state || '').toUpperCase()
  );
};
